import { toUserMessage } from '../../lib/errors.js';
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { closeOutline, trashOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useEnqueueMaster } from '../../lib/data/queries.js';
import { useCelebrate } from '../CelebrationToast.js';
import { SupplierItemMatchesPanel } from './SupplierItemMatchesPanel.js';

export interface SupplierEditorValue {
  id: string;
  name: string;
  category?: string;
  piva?: string | null;
  email?: string | null;
  phone?: string | null;
  country?: string | null;
  version?: number;
}

export interface SupplierEditorModalProps {
  isOpen: boolean;
  initial?: SupplierEditorValue;
  initialName?: string;
  onDismiss: () => void;
  onSaved?: (saved: SupplierEditorValue) => void;
  onRequestArchive?: (rec: { id: string; name: string; version: number }) => void;
  onRequestCanonicalMerge: (sourceFoodId: string, targetFoodId: string) => void;
}

interface InternalForm {
  id: string;
  name: string;
  category: string;
  piva: string;
  email: string;
  phone: string;
  country: string;
  version: number;
}

function buildInitialState(initial: SupplierEditorValue | undefined, initialName: string | undefined): InternalForm {
  if (initial) {
    return {
      id: initial.id,
      name: initial.name,
      category: initial.category ?? 'altro',
      piva: initial.piva ?? '',
      email: initial.email ?? '',
      phone: initial.phone ?? '',
      country: initial.country ?? 'IT',
      version: initial.version ?? 0,
    };
  }
  return {
    id: '',
    name: initialName ?? '',
    category: 'altro',
    piva: '',
    email: '',
    phone: '',
    country: 'IT',
    version: 0,
  };
}

export function SupplierEditorModal(props: SupplierEditorModalProps): JSX.Element {
  const {
    isOpen,
    initial,
    initialName,
    onDismiss,
    onSaved,
    onRequestArchive,
    onRequestCanonicalMerge,
  } = props;
  const { profile } = useAuth();
  const createMaster = useEnqueueMaster();

  const [form, setForm] = useState<InternalForm>(() => buildInitialState(initial, initialName));
  const [toast, setToast] = useState<string | null>(null);
  const celebrate = useCelebrate();

  useEffect(() => {
    if (isOpen) setForm(buildInitialState(initial, initialName));
  }, [isOpen, initial, initialName]);

  async function save(): Promise<void> {
    if (!profile?.active_tenant_id) return;
    const name = form.name.trim();
    if (!name) { setToast('Nome richiesto'); return; }
    const id = form.id || crypto.randomUUID();
    const isUpdate = !!form.id;
    try {
      await createMaster.mutateAsync({
        table: 'suppliers', id, op: isUpdate ? 'update' : 'insert', base_version: form.version ?? 0,
        patch: {
          id, tenant_id: profile.active_tenant_id, name,
          category: form.category || 'altro',
          piva: form.piva || null,
          email: form.email || null,
          phone: form.phone || null,
          country: form.country || null,
          auto_created: false,
        },
      });
      celebrate(isUpdate ? 'Fornitore aggiornato' : 'Fornitore aggiunto');
      onSaved?.({
        id, name,
        category: form.category || 'altro',
        piva: form.piva || null,
        email: form.email || null,
        phone: form.phone || null,
        country: form.country || null,
        version: (form.version ?? 0) + 1,
      });
      onDismiss();
    } catch (err: unknown) {
      setToast(toUserMessage(err));
    }
  }

  return (
    <>
      <IonModal data-testid="supplier-editor" isOpen={isOpen} onDidDismiss={onDismiss}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{form.id ? 'Scheda fornitore' : 'Nuovo fornitore'}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={onDismiss}><IonIcon icon={closeOutline} /></IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
          <div style={{ padding: '12px 16px 24px' }}>
            {/* === SEZIONE 1: ANAGRAFICA === */}
            <h2 style={{ margin: '4px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🏢 Anagrafica
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Nome *</IonLabel>
                <IonInput
                  value={form.name}
                  placeholder="Azienda Agricola Verdi"
                  onIonInput={(ev) => setForm((f) => ({ ...f, name: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">P.IVA</IonLabel>
                <IonInput
                  value={form.piva}
                  placeholder="01234567890"
                  inputmode="numeric"
                  onIonInput={(ev) => setForm((f) => ({ ...f, piva: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Paese</IonLabel>
                <IonSelect interface="action-sheet" value={form.country} onIonChange={(ev) => setForm((f) => ({ ...f, country: String(ev.detail.value) }))}>
                  <IonSelectOption value="IT">🇮🇹 Italia</IonSelectOption>
                  <IonSelectOption value="FR">🇫🇷 Francia</IonSelectOption>
                  <IonSelectOption value="DE">🇩🇪 Germania</IonSelectOption>
                  <IonSelectOption value="ES">🇪🇸 Spagna</IonSelectOption>
                  <IonSelectOption value="AT">🇦🇹 Austria</IonSelectOption>
                  <IonSelectOption value="CH">🇨🇭 Svizzera</IonSelectOption>
                  <IonSelectOption value="SI">🇸🇮 Slovenia</IonSelectOption>
                  <IonSelectOption value="HR">🇭🇷 Croazia</IonSelectOption>
                  <IonSelectOption value="GR">🇬🇷 Grecia</IonSelectOption>
                  <IonSelectOption value="PT">🇵🇹 Portogallo</IonSelectOption>
                  <IonSelectOption value="NL">🇳🇱 Olanda</IonSelectOption>
                  <IonSelectOption value="BE">🇧🇪 Belgio</IonSelectOption>
                  <IonSelectOption value="UK">🇬🇧 Regno Unito</IonSelectOption>
                  <IonSelectOption value="US">🇺🇸 Stati Uniti</IonSelectOption>
                </IonSelect>
              </IonItem>
            </div>

            {/* === SEZIONE 2: CATEGORIA (chip) === */}
            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              🏷️ Categoria
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '12px 14px', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {([
                  { v: 'alimentari',  label: 'Alimentari',  emoji: '🛒' },
                  { v: 'bevande',     label: 'Bevande',     emoji: '🍷' },
                  { v: 'ortofrutta',  label: 'Ortofrutta',  emoji: '🥬' },
                  { v: 'carni',       label: 'Carni',       emoji: '🥩' },
                  { v: 'ittici',      label: 'Ittici',      emoji: '🐟' },
                  { v: 'latticini',   label: 'Latticini',   emoji: '🧀' },
                  { v: 'pulizia',     label: 'Pulizia',     emoji: '🧴' },
                  { v: 'servizi',     label: 'Servizi',     emoji: '🛠️' },
                  { v: 'altro',       label: 'Altro',       emoji: '📦' },
                ] as const).map((c) => {
                  const active = form.category === c.v;
                  return (
                    <button
                      key={c.v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, category: c.v }))}
                      style={{
                        border: `1px solid ${active ? '#0066cc' : '#cbd5e1'}`,
                        background: active ? '#0066cc' : '#fff',
                        color: active ? '#fff' : '#475569',
                        borderRadius: 999,
                        padding: '5px 11px',
                        fontSize: 12,
                        fontWeight: active ? 600 : 500,
                        cursor: 'pointer',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                    >
                      <span style={{ fontSize: 13 }}>{c.emoji}</span>
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* === SEZIONE 3: CONTATTI === */}
            <h2 style={{ margin: '20px 4px 8px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              ✉️ Contatti
            </h2>
            <div style={{ background: '#fff', borderRadius: 14, padding: '4px 0', boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)' }}>
              <IonItem lines="full" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Email</IonLabel>
                <IonInput
                  type="email"
                  inputmode="email"
                  placeholder="info@fornitore.it"
                  value={form.email}
                  onIonInput={(ev) => setForm((f) => ({ ...f, email: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
              <IonItem lines="none" style={{ '--background': 'transparent' } as React.CSSProperties}>
                <IonLabel position="stacked">Telefono</IonLabel>
                <IonInput
                  type="tel"
                  inputmode="tel"
                  placeholder="+39 ..."
                  value={form.phone}
                  onIonInput={(ev) => setForm((f) => ({ ...f, phone: String(ev.detail.value ?? '') }))}
                />
              </IonItem>
            </div>

            {/* === AZIONI === */}
            <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
              <IonButton
                expand="block"
                onClick={() => void save()}
                style={{ '--border-radius': '12px', flex: 2, height: 46 } as React.CSSProperties}
              >
                {form.id ? 'Salva modifiche' : 'Crea fornitore'}
              </IonButton>
              <IonButton
                expand="block"
                fill="outline"
                onClick={onDismiss}
                style={{ '--border-radius': '12px', flex: 1, height: 46 } as React.CSSProperties}
              >
                Annulla
              </IonButton>
            </div>
            {form.id && onRequestArchive && (
              <IonButton
                expand="block"
                color="danger"
                fill="clear"
                style={{ '--border-radius': '12px', marginTop: 8 } as React.CSSProperties}
                onClick={() => {
                  onRequestArchive({ id: form.id, name: form.name, version: form.version });
                  onDismiss();
                }}
              >
                <IonIcon icon={trashOutline} slot="start" />
                Archivia fornitore
              </IonButton>
            )}
            {form.id && (
              <SupplierItemMatchesPanel
                supplierId={form.id}
                isOpen={isOpen}
                onRequestCanonicalMerge={onRequestCanonicalMerge}
              />
            )}
          </div>
        </IonContent>
      </IonModal>
      <IonToast isOpen={!!toast} message={toast ?? ''} duration={1500} onDidDismiss={() => setToast(null)} />
    </>
  );
}
